import {useQuery} from '@tanstack/react-query';
import {Box, CircularProgress, Alert} from '@mui/material';
import apiCall from '../../../../utils/api';
import Indicator from '../../components/Indicator';
import Section from '../../components/Section';
import {IDashboardData, SurveySlug} from '../../utils/types';
import {buildApiKey, serializeSlug} from '../../utils/helpers';

interface Props {
    container: string;
    uoaId: string;
    surveyId: string;
}

const buildSurveySlugs = (surveyId: number): SurveySlug[] => [
    {
        numerator: {
            survey_id: surveyId,
            question: 'question2',
            value: 'Item 2',
        },
        operation: 'match',
        perUoa: true,
        key: 'question2_item-2',
        roman: '',
        title: 'Soddisfazione per la qualità dell’offerta culturale a confronto con la media',
        tooltip: '',
    },
    {
        numerator: {
            survey_id: surveyId,
            question: 'question2',
            value: 'Item 4',
        },
        operation: 'match',
        perUoa: true,
        key: 'question2_item-4',
        roman: '',
        title: 'Soddisfazione per l’accessibilità del luogo di svolgimento a confronto con la media',
        tooltip: '',
    },
    {
        numerator: {
            survey_id: surveyId,
            question: 'question2',
            value: 'Item 5',
        },
        operation: 'match',
        perUoa: true,
        key: 'question2_item-5',
        roman: '',
        title: 'Soddisfazione per la novità dell’iniziativa a confronto con la media',
        tooltip: '',
    },
];

const Dashboard2 = ({
    container,
    uoaId,
    surveyId,
}: Props) => {
    const {data, isLoading, isError} = useQuery<IDashboardData>({
        queryKey: [
            'iniziative-dashboard2',
            container,
            uoaId,
            surveyId,
        ],
        queryFn: async () => {
            const surveySlugs = buildSurveySlugs(Number(surveyId));

            const res = await apiCall({
                url: `containers/${container}/uoa/${uoaId}/dashboards/fsv`,
                method: 'POST',
                data: {
                    question_slugs: surveySlugs.map(serializeSlug),
                },
            });

            const result = {} as IDashboardData;

            for (const slug of surveySlugs) {
                const apiKey = buildApiKey(slug);
                result[slug.key] = res.data[apiKey] ?? {uoa: 0, average: 0};
            }

            return result;
        },
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    const surveySlugs = buildSurveySlugs(Number(surveyId));

    const sections = [
        {
            title: 'I. SODDISFAZIONE DEI FRUITORI PER LE INIZIATIVE',
            keys: ['question2_item-2', 'question2_item-4', 'question2_item-5'],
            maxOverride: 100,
            isCount: false,
            tooltip: `I seguenti indicatori misurano la soddisfazione espressa dai fruitori in relazione a tre aspetti chiave dell'iniziativa:
                - Qualità dell’offerta culturale
                - Accessibilità del luogo di svolgimento
                - Novità dell’iniziativa
                Il grafico mostra, per ciascuna iniziativa realizzata, la percentuale di fruitori che hanno espresso soddisfazione per ciascun aspetto, a confronto con la media dello stesso valore per tutte le iniziative realizzate nell’ambito di tutti i progetti.
                Il valore medio viene calcolato sommando le percentuali di soddisfazione rilevate per ciascuna iniziativa e dividendo il totale per il numero complessivo di iniziative.`,
        },
    ];

    return (
        <Box>
            {sections.map((section) => (
                <Section
                    key={section.title}
                    title={section.title}
                    tooltip={section.tooltip}
                >
                    {surveySlugs
                        .filter((s) => section.keys.includes(s.key))
                        .map((slug) => (
                            <Indicator
                                key={slug.key}
                                roman={slug.roman}
                                title={slug.title}
                                tooltip={slug.tooltip}
                                data={data[slug.key]}
                                maxOverride={section.maxOverride}
                                isCount={section.isCount}
                            />
                        ))}
                </Section>
            ))}
        </Box>
    );
};

export default Dashboard2;
